import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Autocomplete,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  InputAdornment,
  Radio,
  RadioGroup,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { toast } from "react-toastify";
import axiosInstance from "../../../axios/axios";
import { END_POINTS } from "../../../shared";

/**
 * Keyless AWS connection for one or more accounts: the customer creates a read-only IAM role
 * with Spark's CloudFormation template in each account, then Spark scans with short-lived
 * credentials from it. No access keys are entered or stored. Each account becomes its own
 * scan, so one account failing never affects the others.
 *
 * Every account ID entered is checked in the background (a "probe"). When all of them have
 * the role, the card only says Connected. The External ID and launch steps appear only while
 * some account has no role yet, and after Launch Spark keeps checking those accounts until
 * their stacks have finished. Admins with many accounts can open the values for a
 * CloudFormation StackSet, which creates the same role in every account of their AWS
 * Organization at once.
 *
 * "Whole AWS Organization" takes the management account instead: Spark checks its role, lists
 * the Organization's active accounts through it, and fills the list with them; everything after
 * that is the same per-account check and one scan per account.
 *
 * All calls reuse the new-scan route ({ aws_role_connection: "setup" | "test" | "organization" }). Errors
 * are shown from a fixed list keyed by the server's reason code, never as server text.
 */

export type RoleTestStatus = "idle" | "loading" | "success" | "error";
export type AwsScope = "accounts" | "organization";

type RoleSetup = {
  spark_account_id: string;
  role_name: string;
  template_url: string;
  is_admin: boolean;
  external_id?: string;
  launch_url?: string;
};

// checking: first look at the account; connected: the role works; missing: no usable role yet.
type AccountCheck = { state: "checking" | "connected" | "missing"; roleArn?: string; reason?: string };
// Whole Organization: listing; needs_role: the management account has no role yet;
// refused: it cannot list an Organization; listed: its accounts are in the list.
type OrgListing = {
  state: "idle" | "listing" | "needs_role" | "refused" | "listed";
  reason?: string;
  total?: number;
  limit?: number;
  skipped?: number;
};
type OrgAccount = { id: string; name: string };

const ACCOUNT_ID_RE = /^\d{12}$/;
const MAX_ACCOUNTS = 20;
const CHECKS_AT_ONCE = 4;
const POLL_EVERY_MS = 20_000;
const POLL_FOR_MS = 10 * 60_000;

const ROLE_MESSAGES: Record<string, string> = {
  invalid_account_id: "Enter the 12-digit AWS account ID.",
  invalid_role_arn:
    "Enter the role ARN from the CloudFormation stack outputs, for example arn:aws:iam::123456789012:role/SparkSecurityScanRole.",
  account_mismatch: "The role belongs to a different AWS account than the account IDs you entered.",
  assume_denied:
    "Spark could not use the role. Check the role ARN, and that the role trusts Spark's AWS account with the External ID shown here.",
  no_external_id: "An organization administrator must open the IAM role setup first.",
  spark_not_configured: "IAM role connections are not set up on this Spark server yet. Contact Spark support.",
  aws_unreachable: "Spark could not reach AWS to check the role. Try again in a few minutes.",
  not_management_account:
    "This account cannot list the accounts of an AWS Organization. Enter your AWS Organizations management account.",
  organization_list_failed: "Spark could not list the accounts of your AWS Organization. Try again in a few minutes.",
};
const ORG_LIST_ERROR = "We couldn't list your AWS Organization's accounts. Please try again in a moment.";
// Background-check answers that are a real problem rather than "no role yet".
const PROBE_PROBLEMS = new Set(["spark_not_configured", "aws_unreachable"]);
const NOT_READY_ADMIN =
  "Not connected yet. If the stack has finished, check that it was created with the External ID above.";
const NOT_READY_MEMBER = "Not connected yet. Ask an organization administrator to create the role in the account.";
const DEFAULT_TEST_ERROR = "We couldn't test the connection. Please try again in a moment.";
const SETUP_LOAD_ERROR = "We couldn't load the IAM role setup. Please try again in a moment.";

type ApiError = { response?: { data?: { data?: { reason?: string } } } };
const reasonOf = (error: unknown): string | undefined => (error as ApiError)?.response?.data?.data?.reason;
const roleNameOf = (arn: string) => arn.split("/").pop() || arn;
const arnAccountOf = (arn: string) => /^arn:aws:iam::(\d{12}):role\//.exec(arn.trim())?.[1];
const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);

/* AWS IAM Identity Center (SSO): a console link only works with a console session open; without
 * one AWS shows its root / IAM-user sign-in page, which SSO users cannot use. The access portal's
 * shortcut link signs in through SSO into a given account and role, then opens the page. */
const SSO_PORTAL_RE = /^(?:https:\/\/)?([a-z0-9-]+\.awsapps\.com)\/start/i;
const SSO_PORTAL_KEY = "spark.aws.ssoPortal";
const SSO_ROLE_KEY = "spark.aws.ssoRole";
const DEFAULT_SSO_ROLE = "AdministratorAccess";
const STACKSETS_CONSOLE_URL = "https://console.aws.amazon.com/cloudformation/home?region=us-east-1#/stacksets";
const MAX_LAUNCH_BUTTONS = 8;
const portalOf = (text: string) => {
  const match = SSO_PORTAL_RE.exec(text.trim());
  return match ? `https://${match[1].toLowerCase()}/start` : "";
};
const ssoLink = (portal: string, accountId: string, role: string, destination: string) =>
  `${portal}/#/console?account_id=${accountId}&role_name=${encodeURIComponent(role)}&destination=${encodeURIComponent(destination)}`;
const readSaved = (key: string) => {
  try {
    return window.localStorage.getItem(key) || "";
  } catch {
    return "";
  }
};
const save = (key: string, value: string) => {
  try {
    if (value) window.localStorage.setItem(key, value);
    else window.localStorage.removeItem(key);
  } catch {
    /* storage unavailable: the fields simply are not remembered */
  }
};

/** Account IDs from typed or pasted text: separated by spaces, commas, semicolons or new
 * lines; the AWS console's 1234-5678-9012 form is accepted. */
const parseAccountIds = (text: string) =>
  text.split(/[\s,;]+/).map((t) => t.replace(/-/g, "")).filter(Boolean);

type Props = {
  scope: AwsScope;
  accountIds: string[];
  onAccountIdsChange: (ids: string[]) => void;
  roleArn: string;
  onRoleArnChange: (value: string) => void;
  status: RoleTestStatus;
  onStatusChange: (status: RoleTestStatus) => void;
  fieldSx?: SxProps<Theme>;
};

const mutedText = { fontSize: 13, color: "#534F7A" } as const;
const buttonSx = { alignSelf: "flex-start", textTransform: "none", borderRadius: "8px" } as const;

const AwsRoleConnection: React.FC<Props> = ({
  scope,
  accountIds,
  onAccountIdsChange,
  roleArn,
  onRoleArnChange,
  status,
  onStatusChange,
  fieldSx,
}) => {
  const [setup, setSetup] = useState<RoleSetup | null>(null);
  const [setupError, setSetupError] = useState("");
  const [checks, setChecks] = useState<Record<string, AccountCheck>>({});
  const [inputValue, setInputValue] = useState("");
  const [inputError, setInputError] = useState("");
  const [rechecking, setRechecking] = useState(false);
  const [checkedByHand, setCheckedByHand] = useState(false);
  const [pollUntil, setPollUntil] = useState<number | null>(null);
  const [pollStopped, setPollStopped] = useState(false);
  const [ssoPortalText, setSsoPortalText] = useState(() => readSaved(SSO_PORTAL_KEY));
  const [ssoRole, setSsoRole] = useState(() => readSaved(SSO_ROLE_KEY) || DEFAULT_SSO_ROLE);
  const [ssoMode, setSsoMode] = useState(() => !!readSaved(SSO_PORTAL_KEY));
  const [showMore, setShowMore] = useState(false);
  const [testError, setTestError] = useState("");
  const [managementId, setManagementId] = useState("");
  const [org, setOrg] = useState<OrgListing>({ state: "idle" });
  const [accountNames, setAccountNames] = useState<Record<string, string>>({});

  // Accounts still in the list (answers for removed ones are dropped), and those already
  // checked or being checked, so each new account is looked at exactly once.
  const alive = useRef(new Set<string>());
  const known = useRef(new Set<string>());
  const checksNow = useRef(checks);
  const rechecks = useRef(false);
  const unmounted = useRef(false);
  const statusChange = useRef(onStatusChange);
  const accountsChange = useRef(onAccountIdsChange);
  const orgSeq = useRef(0);
  useEffect(() => {
    statusChange.current = onStatusChange;
    accountsChange.current = onAccountIdsChange;
  });
  useEffect(() => {
    checksNow.current = checks;
  }, [checks]);
  useEffect(
    () => () => {
      unmounted.current = true; // closed or switched to access keys: ignore answers still on the way
    },
    [],
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axiosInstance.post(END_POINTS.NEW_SCAN_TYPES, { aws_role_connection: "setup" });
        if (!cancelled) setSetup(res?.data?.data || null);
      } catch (error) {
        console.error("Failed to load the AWS IAM role setup", error);
        if (!cancelled) setSetupError(ROLE_MESSAGES[reasonOf(error) || ""] || SETUP_LOAD_ERROR);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const checkAccount = useCallback(async (id: string): Promise<AccountCheck> => {
    try {
      const res = await axiosInstance.post(END_POINTS.NEW_SCAN_TYPES, {
        aws_role_connection: "test",
        probe: true,
        account_id: id,
      });
      const data = res?.data?.data;
      if (data?.connected) return { state: "connected", roleArn: data.role_arn || "" };
      return { state: "missing", reason: data?.reason || "" };
    } catch (error) {
      console.error("AWS IAM role background check failed", error);
      return { state: "missing", reason: reasonOf(error) || "" };
    }
  }, []);

  // A few accounts at a time, so a long list does not flood the server.
  const checkAccounts = useCallback(
    async (ids: string[]) => {
      const queue = [...ids];
      const worker = async () => {
        for (let id = queue.shift(); id !== undefined; id = queue.shift()) {
          const result = await checkAccount(id);
          if (unmounted.current || !alive.current.has(id)) continue;
          const accountId = id;
          setChecks((prev) => (prev[accountId] ? { ...prev, [accountId]: result } : prev));
        }
      };
      await Promise.all(Array.from({ length: Math.min(CHECKS_AT_ONCE, queue.length) }, worker));
    },
    [checkAccount],
  );

  // New accounts in the list are checked once; removed ones are forgotten.
  useEffect(() => {
    alive.current = new Set(accountIds);
    for (const id of [...known.current]) if (!alive.current.has(id)) known.current.delete(id);
    const fresh = accountIds.filter((id) => !known.current.has(id));
    fresh.forEach((id) => known.current.add(id));
    setChecks((prev) => {
      const next: Record<string, AccountCheck> = {};
      for (const id of accountIds) next[id] = prev[id] || { state: "checking" };
      return next;
    });
    if (fresh.length) checkAccounts(fresh);
  }, [accountIds, checkAccounts]);

  const connectedIds = accountIds.filter((id) => checks[id]?.state === "connected");
  const missingIds = accountIds.filter((id) => checks[id]?.state === "missing");
  const checkingIds = accountIds.filter((id) => (checks[id]?.state ?? "checking") === "checking");
  const allConnected = accountIds.length > 0 && connectedIds.length === accountIds.length;

  useEffect(() => {
    statusChange.current(allConnected ? "success" : "idle");
    if (allConnected) setPollUntil(null);
  }, [allConnected]);

  // manual: "Check again"; otherwise a poll. Only accounts without a role are checked again.
  const recheck = useCallback(
    async (manual: boolean) => {
      if (rechecks.current) return;
      const missing = Object.keys(checksNow.current).filter((id) => checksNow.current[id].state === "missing");
      if (!missing.length) return;
      rechecks.current = true;
      setRechecking(true);
      await checkAccounts(missing);
      rechecks.current = false;
      if (unmounted.current) return;
      setRechecking(false);
      if (manual) setCheckedByHand(true);
    },
    [checkAccounts],
  );

  // Whole Organization: check the management account's role and list the active accounts.
  // A newer answer (another management account typed since) wins.
  const listOrganization = useCallback(async (id: string, again: boolean) => {
    const seq = ++orgSeq.current;
    if (!again) setOrg({ state: "listing" });
    let next: OrgListing;
    let accounts: OrgAccount[] = [];
    try {
      const res = await axiosInstance.post(END_POINTS.NEW_SCAN_TYPES, {
        aws_role_connection: "organization",
        account_id: id,
      });
      const data = res?.data?.data;
      if (!data?.connected) next = { state: "needs_role", reason: data?.reason || "" };
      else if (!data.listed) next = { state: "refused", reason: data.reason || "" };
      else {
        accounts = Array.isArray(data.accounts) ? data.accounts : [];
        next = { state: "listed", total: data.total, limit: data.limit, skipped: data.skipped_inactive };
      }
    } catch (error) {
      console.error("AWS Organization listing failed", error);
      next = { state: "refused", reason: reasonOf(error) || "" };
    }
    if (seq !== orgSeq.current || unmounted.current) return;
    setOrg(next);
    if (next.state === "listed") {
      setPollUntil(null);
      setAccountNames(Object.fromEntries(accounts.map((a) => [String(a.id), a.name || ""])));
      accountsChange.current(accounts.map((a) => String(a.id)));
    }
  }, []);

  useEffect(() => {
    if (scope !== "organization") return;
    orgSeq.current += 1; // drop answers for the previous management account
    setOrg({ state: "idle" });
    setPollUntil(null);
    setPollStopped(false);
    accountsChange.current([]);
    if (!ACCOUNT_ID_RE.test(managementId)) return;
    const timer = window.setTimeout(() => listOrganization(managementId, false), 400);
    return () => window.clearTimeout(timer);
  }, [scope, managementId, listOrganization]);

  // After Launch: check every 20 seconds, and at once when the user comes back to this tab,
  // until every account is connected or 10 minutes have passed.
  useEffect(() => {
    if (pollUntil === null) return;
    const tick = () => {
      if (Date.now() >= pollUntil) {
        setPollUntil(null);
        setPollStopped(true);
        return;
      }
      if (scope === "organization" && org.state === "needs_role") listOrganization(managementId, true);
      else recheck(false);
    };
    const onReturn = () => {
      if (document.visibilityState === "visible") tick();
    };
    const timer = window.setInterval(tick, POLL_EVERY_MS);
    document.addEventListener("visibilitychange", onReturn);
    window.addEventListener("focus", onReturn);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onReturn);
      window.removeEventListener("focus", onReturn);
    };
  }, [pollUntil, recheck, scope, org.state, managementId, listOrganization]);

  const startPolling = () => {
    setPollStopped(false);
    setCheckedByHand(false);
    setPollUntil(Date.now() + POLL_FOR_MS);
  };

  /** Add the account IDs in `text`; keeps the text in the box when one of them is invalid. */
  const addAccounts = (text: string) => {
    const tokens = parseAccountIds(text);
    if (!tokens.length) {
      setInputValue("");
      return true;
    }
    const valid = tokens.filter((t) => ACCOUNT_ID_RE.test(t));
    const invalid = tokens.filter((t) => !ACCOUNT_ID_RE.test(t));
    const merged = [...accountIds];
    valid.forEach((id) => {
      if (!merged.includes(id)) merged.push(id);
    });
    if (merged.length !== accountIds.length) onAccountIdsChange(merged.slice(0, MAX_ACCOUNTS));
    if (invalid.length) {
      setInputError(`"${invalid[0]}" isn't a 12-digit AWS account ID.`);
      setInputValue(invalid.join(" "));
      return false;
    }
    setInputError(merged.length > MAX_ACCOUNTS ? `Up to ${MAX_ACCOUNTS} accounts at a time.` : "");
    setInputValue("");
    return true;
  };

  const copyValue = async (label: string, value?: string) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} copied`);
    } catch {
      toast.error(`Couldn't copy. Select the ${label} and copy it manually.`);
    }
  };

  const copyField = (label: string, value: string, helperText?: string) => (
    <TextField
      label={label}
      value={value}
      size="small"
      fullWidth
      sx={fieldSx}
      InputProps={{
        readOnly: true,
        endAdornment: (
          <InputAdornment position="end">
            <Tooltip title={`Copy ${label}`}>
              <Button size="small" onClick={() => copyValue(label, value)} sx={{ minWidth: 0 }} aria-label={`Copy ${label}`}>
                <ContentCopyIcon sx={{ fontSize: 18 }} />
              </Button>
            </Tooltip>
          </InputAdornment>
        ),
      }}
      helperText={helperText}
    />
  );

  // A role with another name: the strict test, with a reason when it fails. The role ARN
  // names its account, which must be one of the accounts entered.
  const testConnection = async () => {
    setTestError("");
    const arn = roleArn.trim();
    const account = arnAccountOf(arn);
    if (!account || !accountIds.includes(account)) {
      setTestError(ROLE_MESSAGES[account ? "account_mismatch" : "invalid_role_arn"]);
      onStatusChange("error");
      return;
    }
    onStatusChange("loading");
    try {
      const res = await axiosInstance.post(END_POINTS.NEW_SCAN_TYPES, {
        aws_role_connection: "test",
        account_id: account,
        role_arn: arn,
      });
      const connected: AccountCheck = { state: "connected", roleArn: res?.data?.data?.role_arn || arn };
      setChecks((prev) => (prev[account] ? { ...prev, [account]: connected } : prev));
      onStatusChange("idle"); // "success" follows once every account is connected
    } catch (error) {
      console.error("AWS IAM role connection test failed", error);
      setTestError(ROLE_MESSAGES[reasonOf(error) || ""] || DEFAULT_TEST_ERROR);
      onStatusChange("error");
    }
  };

  const chipFor = (id: string) => {
    const state = checks[id]?.state ?? "checking";
    const icon =
      state === "connected" ? (
        <CheckCircleIcon sx={{ color: "#16A34A !important" }} />
      ) : state === "missing" ? (
        <ErrorOutlineIcon sx={{ color: "#D97706 !important" }} />
      ) : (
        <CircularProgress size={14} sx={{ ml: "6px" }} />
      );
    const tip =
      state === "connected"
        ? `Connected with ${roleNameOf(checks[id]?.roleArn || "") || "the IAM role"}`
        : state === "missing"
          ? "Not connected yet"
          : "Checking…";
    return { icon, tip };
  };

  const accountField = (
    <Autocomplete<string, true, false, true>
      multiple
      freeSolo
      options={[]}
      value={accountIds}
      inputValue={inputValue}
      onChange={(_, values, reason) => {
        // Enter: MUI has already cleared the box; an invalid ID is put back for correcting.
        if (reason === "createOption") {
          addAccounts(String(values[values.length - 1] ?? ""));
          return;
        }
        onAccountIdsChange(values.map(String));
      }}
      onInputChange={(_, value, reason) => {
        if (reason !== "input") {
          setInputValue(value);
          return;
        }
        setInputError("");
        // A separator ends an ID: add what was typed before it, keep the rest in the box.
        const lastSeparator = Math.max(value.lastIndexOf(" "), value.lastIndexOf(","), value.lastIndexOf(";"));
        if (lastSeparator >= 0) {
          const done = addAccounts(value.slice(0, lastSeparator));
          if (done) setInputValue(value.slice(lastSeparator + 1));
          return;
        }
        setInputValue(value);
      }}
      renderTags={(values, getTagProps) =>
        values.map((id, index) => {
          const { key, ...tagProps } = getTagProps({ index });
          const { icon, tip } = chipFor(id);
          return (
            <Tooltip key={key} title={tip}>
              <Chip {...tagProps} size="small" icon={icon} label={id} />
            </Tooltip>
          );
        })
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label="AWS account IDs"
          placeholder={accountIds.length ? "" : "123456789012"}
          size="small"
          sx={fieldSx}
          error={!!inputError}
          helperText={inputError || "Press Enter after each ID"}
          onBlur={() => {
            if (inputValue.trim()) addAccounts(inputValue);
          }}
          inputProps={{
            ...params.inputProps,
            onPaste: (e: React.ClipboardEvent<HTMLInputElement>) => {
              const text = e.clipboardData.getData("text");
              if (/[\s,;]/.test(text.trim())) {
                e.preventDefault();
                addAccounts(inputValue + text);
              }
            },
          }}
        />
      )}
    />
  );

  const spinnerLine = (text: string) => (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <CircularProgress size={16} />
      <Typography sx={{ fontSize: 13 }}>{text}</Typography>
    </div>
  );

  const isOrg = scope === "organization";
  const count = accountIds.length;
  const canTest = count > 0 && roleArn.trim().length > 0 && status !== "loading";
  const exampleArn = `arn:aws:iam::${missingIds[0] || accountIds[0] || "123456789012"}:role/${
    setup?.role_name || "SparkSecurityScanRole"
  }`;
  const polling = pollUntil !== null;
  const orgNeedsRole = isOrg && org.state === "needs_role";
  // "No role yet" is only worth saying after the user asked; real problems always show.
  const reasons = orgNeedsRole ? [org.reason || ""] : missingIds.map((id) => checks[id]?.reason || "");
  const problem = reasons.find((r) => PROBE_PROBLEMS.has(r));
  let probeNote = "";
  if (problem) probeNote = ROLE_MESSAGES[problem];
  else if (checkedByHand && reasons.includes("no_external_id")) probeNote = ROLE_MESSAGES.no_external_id;
  else if (checkedByHand) probeNote = setup?.is_admin ? NOT_READY_ADMIN : NOT_READY_MEMBER;

  const managementField = (
    <TextField
      label="Management account ID"
      value={managementId}
      onChange={(e) => {
        setManagementId(e.target.value.replace(/[^0-9]/g, "").slice(0, 12));
        setCheckedByHand(false);
      }}
      placeholder="123456789012"
      size="small"
      fullWidth
      sx={fieldSx}
    />
  );

  // Organization accounts: no typing, but one can be left out of this run.
  const orgChips = (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
      {accountIds.map((id) => {
        const { icon, tip } = chipFor(id);
        const name = accountNames[id];
        return (
          <Tooltip key={id} title={name ? `${name}: ${tip}` : tip}>
            <Chip
              size="small"
              icon={icon}
              label={name ? `${name} (${id})` : id}
              onDelete={() => onAccountIdsChange(accountIds.filter((x) => x !== id))}
            />
          </Tooltip>
        );
      })}
    </div>
  );

  const found = org.total ?? count;
  const orgSummary =
    org.state === "listed" ? (
      <Typography sx={mutedText}>
        {found === 0 ? "No active accounts found." : `${found} ${plural(found, "account", "accounts")} found`}
        {org.total !== undefined && org.limit !== undefined && org.total > org.limit ? ` · first ${org.limit} scanned` : ""}
        {org.skipped ? ` · ${org.skipped} suspended left out` : ""}
      </Typography>
    ) : null;

  const top = isOrg ? (
    <>
      {managementField}
      {orgSummary}
      {org.state === "listed" && count > 0 && orgChips}
    </>
  ) : (
    accountField
  );

  const ssoPortal = portalOf(ssoPortalText);
  const ssoRoleName = ssoRole.trim() || DEFAULT_SSO_ROLE;
  // The accounts that still need the stack: the management account in Organization mode.
  const targets = orgNeedsRole ? [managementId].filter((id) => ACCOUNT_ID_RE.test(id)) : missingIds;
  const accountLabel = (id: string) => (accountNames[id] ? `${accountNames[id]} (${id})` : id);

  const stepTitle = (n: number, text: string) => (
    <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
      {n}. {text}
    </Typography>
  );

  const launchButtons = (launchUrl: string) =>
    ssoMode && ssoPortal && targets.length > 0 ? (
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
        {targets.slice(0, MAX_LAUNCH_BUTTONS).map((id) => (
          <Button
            key={id}
            variant="contained"
            href={ssoLink(ssoPortal, id, ssoRoleName, launchUrl)}
            target="_blank"
            rel="noopener noreferrer"
            endIcon={<OpenInNewIcon />}
            onClick={startPolling}
            sx={buttonSx}
          >
            Launch in {targets.length > 1 ? accountLabel(id) : "AWS"}
          </Button>
        ))}
      </div>
    ) : (
      <Button
        variant="contained"
        href={launchUrl}
        target="_blank"
        rel="noopener noreferrer"
        endIcon={<OpenInNewIcon />}
        onClick={startPolling}
        disabled={ssoMode && !ssoPortal}
        sx={buttonSx}
      >
        Launch in AWS
      </Button>
    );

  const signInChoice = (
    <>
      <RadioGroup
        row
        value={ssoMode ? "sso" : "aws"}
        onChange={(e) => setSsoMode(e.target.value === "sso")}
        sx={{ gap: 1 }}
      >
        <FormControlLabel value="aws" control={<Radio size="small" />} label={<Typography sx={{ fontSize: 13 }}>AWS login</Typography>} />
        <FormControlLabel value="sso" control={<Radio size="small" />} label={<Typography sx={{ fontSize: 13 }}>Company SSO</Typography>} />
      </RadioGroup>
      {ssoMode && (
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          <TextField
            label="SSO portal URL"
            value={ssoPortalText}
            onChange={(e) => {
              setSsoPortalText(e.target.value);
              save(SSO_PORTAL_KEY, portalOf(e.target.value));
            }}
            placeholder="https://d-xxxxxxxxxx.awsapps.com/start"
            error={!!ssoPortalText.trim() && !ssoPortal}
            helperText={ssoPortalText.trim() && !ssoPortal ? "Like https://d-xxxxxxxxxx.awsapps.com/start" : undefined}
            size="small"
            sx={{ ...fieldSx, flex: "2 1 240px" }}
          />
          <TextField
            label="Admin role"
            value={ssoRole}
            onChange={(e) => {
              setSsoRole(e.target.value);
              save(SSO_ROLE_KEY, e.target.value.trim());
            }}
            size="small"
            sx={{ ...fieldSx, flex: "1 1 160px" }}
          />
        </div>
      )}
    </>
  );

  const moreOptions = setup && (
    <>
      <Button
        variant="text"
        onClick={() => setShowMore((v) => !v)}
        startIcon={showMore ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        sx={{ ...buttonSx, px: 0, color: "#534F7A" }}
      >
        More options
      </Button>
      {showMore && (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {setup.is_admin && setup.external_id && copyField("External ID", setup.external_id)}
          {setup.is_admin && setup.external_id && (
            <>
              <Typography sx={{ fontSize: 13, fontWeight: 600 }}>Many accounts: one StackSet</Typography>
              {isOrg && ssoMode && ssoPortal && ACCOUNT_ID_RE.test(managementId) && (
                <Button
                  variant="outlined"
                  href={ssoLink(ssoPortal, managementId, ssoRoleName, STACKSETS_CONSOLE_URL)}
                  target="_blank"
                  rel="noopener noreferrer"
                  endIcon={<OpenInNewIcon />}
                  sx={buttonSx}
                >
                  Open StackSets
                </Button>
              )}
              {copyField("Template URL", setup.template_url)}
              {copyField("SparkAccountId", setup.spark_account_id)}
              <Typography sx={{ fontSize: 12, color: "#534F7A" }}>
                Service-managed permissions, automatic deployment on, one region. Use the External ID above.
              </Typography>
            </>
          )}
          {!orgNeedsRole && connectedIds.length > 0 && missingIds.length > 0 && (
            <Button variant="outlined" onClick={() => onAccountIdsChange(connectedIds)} sx={buttonSx}>
              Scan only the {connectedIds.length} connected {plural(connectedIds.length, "account", "accounts")}
            </Button>
          )}
          {!orgNeedsRole && (
            <>
              <TextField
                label="Different role? Role ARN"
                value={roleArn}
                onChange={(e) => {
                  onRoleArnChange(e.target.value);
                  if (status !== "idle") onStatusChange("idle");
                  setTestError("");
                }}
                placeholder={exampleArn}
                size="small"
                fullWidth
                sx={fieldSx}
              />
              <Button variant="outlined" onClick={testConnection} disabled={!canTest} sx={buttonSx}>
                {status === "loading" ? <CircularProgress size={18} color="inherit" /> : "Test connection"}
              </Button>
            </>
          )}
        </div>
      )}
    </>
  );

  const header = (
    <div className="nsd-credential-card-header" style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <div className="nsd-credential-card-title">Connect with an IAM role</div>
      <Tooltip title="Spark scans with short-lived credentials from a read-only role in each account. No keys are stored; deleting the role removes Spark's access.">
        <InfoOutlinedIcon sx={{ fontSize: 16, color: "#888994" }} />
      </Tooltip>
    </div>
  );

  const setupCard = () => (
    <div className="nsd-credential-card">
      {header}
      <div className="nsd-credential-card-body" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {top}
        {!orgNeedsRole && missingIds.length > 0 && count > 1 && (
          <Typography sx={mutedText}>
            Needs the Spark role: {missingIds.join(", ")}
          </Typography>
        )}
        {!orgNeedsRole && checkingIds.length > 0 && spinnerLine(`Checking ${checkingIds.length}…`)}
        {setupError && <Typography sx={{ fontSize: 13, color: "#b91c1c" }}>{setupError}</Typography>}
        {!setup && !setupError && spinnerLine("Loading…")}

        {setup && setup.is_admin && setup.launch_url && (
          <>
            {stepTitle(1, orgNeedsRole ? "Create the Spark role in the management account" : "Create the Spark role in AWS")}
            {signInChoice}
            {launchButtons(setup.launch_url)}
          </>
        )}
        {setup && !setup.is_admin && (
          <Typography sx={mutedText}>Ask an organization admin to create the Spark role.</Typography>
        )}

        {setup && (
          <>
            {stepTitle(setup.is_admin ? 2 : 1, orgNeedsRole ? "Spark lists your accounts automatically" : "Spark connects automatically")}
            <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              {polling ? (
                spinnerLine("Waiting for the stack…")
              ) : (
                <Typography sx={mutedText}>{pollStopped ? "Stopped waiting." : "Not connected yet."}</Typography>
              )}
              <Button
                variant="outlined"
                size="small"
                onClick={async () => {
                  if (!orgNeedsRole) return recheck(true);
                  setRechecking(true);
                  await listOrganization(managementId, true);
                  if (unmounted.current) return;
                  setRechecking(false);
                  setCheckedByHand(true);
                }}
                disabled={rechecking}
                sx={buttonSx}
              >
                {rechecking ? <CircularProgress size={16} color="inherit" /> : "Check again"}
              </Button>
            </div>
            {probeNote && <Typography sx={{ fontSize: 13, color: "#b91c1c" }}>{probeNote}</Typography>}
          </>
        )}

        {moreOptions}

        {status === "error" && (
          <div className="nsd-status nsd-status-error" style={{ marginTop: 0 }}>
            <div className="nsd-status-icon" aria-hidden="true" />
            <div className="nsd-status-text">
              <div className="nsd-status-title">Connection Failed</div>
              <div className="nsd-status-subtitle">{testError || DEFAULT_TEST_ERROR}</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const plainCard = (children: React.ReactNode) => (
    <div className="nsd-credential-card">
      {header}
      <div className="nsd-credential-card-body">
        {top}
        {children}
      </div>
    </div>
  );

  // Whole Organization, before its accounts are in the list.
  if (isOrg && org.state !== "listed") {
    if (org.state === "needs_role") return setupCard();
    if (org.state === "listing") return plainCard(spinnerLine("Finding your accounts…"));
    if (org.state === "refused") {
      return plainCard(
        <Typography sx={{ fontSize: 13, color: "#b91c1c" }}>{ROLE_MESSAGES[org.reason || ""] || ORG_LIST_ERROR}</Typography>,
      );
    }
    return plainCard(null);
  }

  if (count === 0) return plainCard(null);
  if (missingIds.length === 0 && !allConnected) {
    return plainCard(spinnerLine(count === 1 ? "Checking…" : `Checking… ${connectedIds.length} of ${count} connected`));
  }

  if (allConnected) {
    return plainCard(
      <div className="nsd-status nsd-status-success" style={{ marginTop: 0 }}>
        <div className="nsd-status-icon" aria-hidden="true">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="10" cy="10" r="8" fill="#ffffff" stroke="#16A34A" strokeWidth="2" />
            <path d="M7.2 10.4L9 12.2L12.8 8.4" stroke="#16A34A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
        <div className="nsd-status-text">
          <div className="nsd-status-title">
            Connected{count > 1 ? ` · ${count} accounts` : ""}
          </div>
        </div>
      </div>,
    );
  }

  return setupCard();
};

export default AwsRoleConnection;
